/**
 * Agent Manager
 * Receives agent data from parent window via postMessage and notifies characters
 */

import * as PIXI from 'pixi.js';

/**
 * Agent status colors
 */
const STATUS_COLORS = {
  idle: 0x48bb78,    // Green
  active: 0x4299e1,  // Blue  
  blocked: 0xe53e3e  // Red
};

/**
 * Agent role emojis/initials mapping
 */
const ROLE_ICONS = {
  'CEO': '👑',
  'CTO': '⚙️',
  'Product Analyst': '📊',
  'Lead Dev': '💻',
  'Backend Dev': '🔧',
  'Frontend Dev': '🎨',
  'DevOps': '🚀',
  'QA': '🧪',
  'Designer': '🎨',
  'default': '👤'
};

/**
 * Agent representation on the grid
 */
export class Agent {
  constructor(data, gridX, gridY) {
    this.id = data._id;
    this.name = data.name;
    this.role = data.role;
    this.status = data.status;
    this.currentTaskId = data.currentTaskId;
    this.sessionKey = data.sessionKey;
    
    // Grid position (can be assigned based on role/availability)
    this.gridX = gridX;
    this.gridY = gridY;
    
    // Visual elements
    this.container = null;
    this.avatar = null;
    this.statusIndicator = null;
    this.nameLabel = null;
    
    // Animation
    this.targetScale = 1;
    this.currentScale = 0;
    this.isEntering = true;
  }

  /**
   * Create the visual representation of the agent
   */
  createVisuals(tileWidth, tileHeight) {
    this.container = new PIXI.Container();
    
    const halfWidth = tileWidth / 2;
    const halfHeight = tileHeight / 2;
    
    // Agent circle/avatar background
    const avatarBg = new PIXI.Graphics();
    avatarBg.beginFill(STATUS_COLORS[this.status] || STATUS_COLORS.idle);
    avatarBg.drawCircle(0, -halfHeight - 15, 20);
    avatarBg.endFill();
    
    // Border
    avatarBg.lineStyle(2, 0xffffff, 0.8);
    avatarBg.drawCircle(0, -halfHeight - 15, 20);
    
    this.avatar = avatarBg;
    this.container.addChild(avatarBg);
    
    // Role icon/initials (simplified as text for now)
    const icon = ROLE_ICONS[this.role] || ROLE_ICONS.default;
    const iconText = new PIXI.Text(icon, {
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
      align: 'center'
    });
    iconText.anchor.set(0.5);
    iconText.position.set(0, -halfHeight - 15);
    this.container.addChild(iconText);
    
    // Status indicator (small dot)
    const statusIndicator = new PIXI.Graphics();
    statusIndicator.beginFill(STATUS_COLORS[this.status]);
    statusIndicator.drawCircle(0, 0, 6);
    statusIndicator.endFill();
    statusIndicator.position.set(12, -halfHeight - 5);
    
    // Pulse animation for active agents
    if (this.status === 'active') {
      statusIndicator.alpha = 1;
    }
    
    this.statusIndicator = statusIndicator;
    this.container.addChild(statusIndicator);
    
    // Name label
    const nameLabel = new PIXI.Text(this.name, {
      fontFamily: 'Arial',
      fontSize: 10,
      fill: 0xffffff,
      align: 'center',
      dropShadow: true,
      dropShadowColor: 0x000000,
      dropShadowDistance: 1,
      dropShadowBlur: 2
    });
    nameLabel.anchor.set(0.5);
    nameLabel.position.set(0, -halfHeight - 40);
    this.nameLabel = nameLabel;
    this.container.addChild(nameLabel);
    
    // Status text
    const statusText = new PIXI.Text(this.status, {
      fontFamily: 'Arial',
      fontSize: 8,
      fill: STATUS_COLORS[this.status],
      align: 'center'
    });
    statusText.anchor.set(0.5);
    statusText.position.set(0, -halfHeight - 52);
    this.statusText = statusText;
    this.container.addChild(statusText);
    
    // Set initial scale for entrance animation
    this.container.scale.set(0);
    
    // Make interactive
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
    
    this.container.on('pointerover', () => {
      this.targetScale = 1.2;
      this.nameLabel.style.fontSize = 12;
    });
    
    this.container.on('pointerout', () => {
      this.targetScale = 1;
      this.nameLabel.style.fontSize = 10;
    });
    
    return this.container;
  }

  /**
   * Update agent data
   */
  update(data) {
    const oldStatus = this.status;
    this.status = data.status;
    this.currentTaskId = data.currentTaskId;
    
    // Update visuals if status changed
    if (oldStatus !== this.status) {
      this.updateStatusVisuals();
    }
  }

  /**
   * Update status-related visuals
   */
  updateStatusVisuals() {
    if (!this.avatar || !this.statusIndicator) return;
    
    const color = STATUS_COLORS[this.status] || STATUS_COLORS.idle;
    
    // Redraw avatar with new color
    this.avatar.clear();
    this.avatar.beginFill(color);
    this.avatar.drawCircle(0, -15, 20);
    this.avatar.endFill();
    this.avatar.lineStyle(2, 0xffffff, 0.8);
    this.avatar.drawCircle(0, -15, 20);
    
    // Update status indicator
    this.statusIndicator.clear();
    this.statusIndicator.beginFill(color);
    this.statusIndicator.drawCircle(0, 0, 6);
    this.statusIndicator.endFill();
    
    // Update status text
    this.statusText.text = this.status;
    this.statusText.style.fill = color;
    
    // Trigger a small pulse animation on status change
    this.pulseAnimation = 0;
  }

  /**
   * Update animation
   */
  update(delta) {
    // Entrance animation
    if (this.isEntering) {
      this.currentScale += (this.targetScale - this.currentScale) * 0.1;
      if (Math.abs(this.targetScale - this.currentScale) < 0.01) {
        this.currentScale = this.targetScale;
        this.isEntering = false;
      }
      this.container.scale.set(this.currentScale);
    } else {
      // Normal scale animation (hover effects)
      const current = this.container.scale.x;
      const diff = this.targetScale - current;
      if (Math.abs(diff) > 0.001) {
        this.container.scale.set(current + diff * 0.2);
      }
    }
    
    // Pulse animation for active status
    if (this.status === 'active' && this.statusIndicator) {
      this.pulseAnimation = (this.pulseAnimation || 0) + delta * 0.1;
      const pulse = 1 + Math.sin(this.pulseAnimation) * 0.3;
      this.statusIndicator.scale.set(pulse);
      this.statusIndicator.alpha = 0.7 + Math.sin(this.pulseAnimation) * 0.3;
    } else if (this.statusIndicator) {
      this.statusIndicator.scale.set(1);
      this.statusIndicator.alpha = 1;
    }
  }

  /**
   * Destroy agent visuals
   */
  destroy() {
    if (this.container) {
      this.container.destroy({ children: true });
    }
  }
}

/**
 * Agent Manager - Manages all agents and receives data from parent window
 * No longer renders visual avatars - characters handle their own visuals
 */
export class AgentManager {
  constructor(onAgentUpdate = null) {
    this.agents = new Map();
    
    // Callback for agent updates (passed to main.js to update characters)
    this.onAgentUpdate = onAgentUpdate;
  }

  /**
   * Handle external agent update from parent window via postMessage
   */
  handleExternalAgentUpdate(agentData) {
    // Store/update agent data
    this.agents.set(agentData.id, agentData);
    
    // Notify callback if provided
    if (this.onAgentUpdate) {
      this.onAgentUpdate(agentData);
    }
  }

  /**
   * Stop the agent manager and clean up
   */
  stop() {
    this.agents.clear();
  }
}

export default AgentManager;
